import PlanSalesService from "@/api/services/planSales";
import { isPurchasedWithin30Days, formatPriceTTC, isVirtualProduct } from "@estaly/ui";

const initialState = {
  membershipAgreementUrl: null,
};

export default {
  namespaced: true,
  state: initialState,
  getters: {
    isPurchasedWithin30Days: () => isPurchasedWithin30Days,
    isActive: () => (plan) => {
      return plan?.status?.toLowerCase() === "active";
    },
    isSuspended: () => (plan) => {
      return plan?.status?.toLowerCase() === "suspended";
    },
    isCancelled: () => (plan) => {
      return plan?.status?.toLowerCase() === "cancelled";
    },
    isExpired: () => (plan) => {
      return plan?.status?.toLowerCase() === "expired";
    },
    statusBadgeClass: () => (plan) => {
      switch (plan?.status?.toLowerCase()) {
        case "active":
          return "bg-green-200 text-green-800";
        case "expired":
          return "bg-gray-200 text-gray-600";
        case "suspended":
          return "bg-gray-200 text-gray-600";
        case "cancelled":
          return "bg-red-200 text-red-800";
        default:
          return "bg-gray-100 text-gray-500";
      }
    },
    statusLabel: () => (plan) => {
      return `plan_sale.status.${plan?.status}`;
    },
    hasBreakdownCoverage: () => (plan) => {
      return plan?.planSaleContract?.planSaleCoverages?.some((c) => c.name === "breakdown") ?? false;
    },
    coverageNames: () => (plan) => {
      return plan?.planSaleContract?.planSaleCoverages?.map((c) => c.name) ?? [];
    },
    hasVirtualProduct: () => (plan) => {
      return plan?.products?.some(isVirtualProduct) ?? false;
    },

    productExchangeAllowed: (_, getters, _rootState, rootGetters) => (plan, claims) => {
      if (!getters.isActive(plan)) return false;
      if (getters.hasVirtualProduct(plan)) return false;
      if (claims === null || claims === undefined) return false;
      const hasActiveClaim = claims.some((claim) => rootGetters["claims/isActiveClaim"](claim));
      return (
        !plan.composedProductType &&
        plan.products?.length === 1 &&
        !hasActiveClaim &&
        getters.isPurchasedWithin30Days(plan.purchaseDate)
      );
    },
    warrantyTerm: () => (plan) => {
      if (!plan?.termLength) return null;
      const base = `${plan.termLength} mois`;
      return plan.recurring ? `${base} renouvelable par tacite reconduction` : base;
    },
    planPrice: () => (plan) => {
      const price = formatPriceTTC(plan?.price);
      if (price == null) return "";
      if (plan?.monthly) return `${price} / mois`;
      if (plan?.recurring) return `${price} / an`;
      return price;
    },
    paymentFrequencyKey: () => (plan) => {
      if (plan?.monthly) return "plan_sale.payment_frequency.monthly";
      if (plan?.recurring) return "plan_sale.payment_frequency.yearly";
      return "plan_sale.payment_frequency.one_time";
    },
    paymentMethodLabel: () => (paymentMethod) => {
      if (!paymentMethod?.brand || !paymentMethod?.digits) return null;
      return `${paymentMethod.brand} **** ${paymentMethod.digits}`;
    },
    composedItemsPrice: () => (plan) => {
      if (plan?.products?.length > 0) {
        const total = plan.products.reduce((sum, product) => {
          const price = parseFloat(String(product.price ?? "0").replace(",", "."));
          return sum + (isNaN(price) ? 0 : price);
        }, 0);
        return total.toFixed(2);
      }
      return "0.00";
    },
    composedItemsPricePercentage: (_, getters) => (plan) => {
      if (plan?.planSaleContract?.maxInsurableCombinedPrice > 0) {
        const percentage =
          (parseFloat(getters.composedItemsPrice(plan)) / plan.planSaleContract.maxInsurableCombinedPrice) * 100;
        return percentage.toFixed(2);
      }
      return "0.00";
    },
    productPrice: () => (plan) => {
      if (!plan?.products?.length) return null;
      const product = plan.products[0];
      return product?.items?.length ? product.items[0].price : product?.price;
    },
    itemTypes: () => (plan) => {
      const ALL_ITEM_TYPES = [
        "phone",
        "tablet",
        "camera",
        "vr_headset",
        "tv",
        "projector",
        "soundbar",
        "headphones",
        "earbuds",
        "speaker",
        "home_cinema",
        "radio",
        "printer",
        "scanner",
        "screen",
        "computer",
        "e_reader",
        "keyboard",
        "mouse",
        "smartwatch",
        "robot",
        "webcam",
        "alarm",
        "game_console",
        "controller",
        "simulator",
        "other_excluding_phones",
      ];
      if (plan?.planSaleContract?.category === "home_plan") {
        return ALL_ITEM_TYPES.filter((t) => t !== "phone");
      }
      return ALL_ITEM_TYPES;
    },
    paymentsUpToDate: () => (plan) => {
      if (!plan?.planSaleBillings?.length) return true;
      return !plan.planSaleBillings.some((b) => b.collected && b.collectible && (b.failed || b.disputed));
    },
  },
  mutations: {
    setMembershipAgreementUrl(state, url) {
      state.membershipAgreementUrl = url;
    },
  },
  actions: {
    async declareProductExchange(_, { friendlyId, referenceId, title, price, purchaseDate }) {
      return await PlanSalesService.declareProductExchange(friendlyId, { referenceId, title, price, purchaseDate });
    },

    async fetchMembershipAgreement({ commit }, friendlyId) {
      const response = await PlanSalesService.getPlanMembershipAgreement(friendlyId);
      if (response?.data?.file_url) {
        commit("setMembershipAgreementUrl", response.data.file_url);
      }
      return response;
    },
  },
};
